import React, { useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  ShippingZone,
  CreateZoneRequest,
  UpdateZoneRequest,
} from "../../model/SiteShipping";
import { X } from "lucide-react";

interface Props {
  zone: ShippingZone | null;
  onClose: (saved: boolean) => void;
}

const ShippingZoneForm: React.FC<Props> = ({ zone, onClose }) => {
  const isEdit = !!zone;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(zone?.name || "");
  const [description, setDescription] = useState(zone?.description || "");
  const [countriesInput, setCountriesInput] = useState(
    zone?.countries?.join(", ") || "",
  );
  const [postcodePrefixesInput, setPostcodePrefixesInput] = useState(
    zone?.postcodePrefixes?.join(", ") || "",
  );
  const [isActive, setIsActive] = useState(zone?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(zone?.sortOrder ?? 0);

  const parseCommaSeparated = (val: string): string[] =>
    val
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const countries = parseCommaSeparated(countriesInput);
      const postcodePrefixes = parseCommaSeparated(postcodePrefixesInput);

      if (isEdit) {
        const body: UpdateZoneRequest = {
          name,
          description,
          countries,
          postcodePrefixes,
          isActive,
          sortOrder,
        };
        await api.put(`${API_URLS.SHIPPING_ZONES}/${zone!.id}`, body);
      } else {
        const body: CreateZoneRequest = {
          name,
          description,
          countries,
          postcodePrefixes,
          isActive,
          sortOrder,
        };
        await api.post(API_URLS.SHIPPING_ZONES, body);
      }
      onClose(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar zona.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isEdit ? "Editar Zona" : "Nova Zona"}
          </h3>
          <button onClick={() => onClose(false)}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descricao
            </label>
            <textarea
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Paises (codigos ISO, separados por virgula)
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={countriesInput}
              onChange={(e) => setCountriesInput(e.target.value)}
              placeholder="PT, ES, FR"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prefixos de Codigo Postal (separados por virgula)
            </label>
            <input
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={postcodePrefixesInput}
              onChange={(e) => setPostcodePrefixesInput(e.target.value)}
              placeholder="1000, 2000, 3000"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ordem
              </label>
              <input
                type="number"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4"
              />
              Ativa
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            className="button is-light is-small"
            onClick={() => onClose(false)}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="button is-primary is-small"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShippingZoneForm;
